import { Assets, Spritesheet, Texture } from 'pixi.js'
import { useEffect, useState } from 'react'

interface StarPixiAssets {
  textures: Texture[]
  isLoaded: boolean
}

const assetPaths = [
  '/assets/stars/default/stars_01-1.json',
  '/assets/stars/default/stars_01-2.json',
  '/assets/stars/default/stars_01-3.json',
  '/assets/stars/default/stars_01-4.json',
  '/assets/stars/default/stars_01-5.json',
  '/assets/stars/default/stars_01-6.json',
  '/assets/stars/default/stars_01-7.json',
]

const loadAssetIfNotCached = async <T>(path: string): Promise<T> => {
  if (Assets.cache.has(path)) {
    return Assets.cache.get(path) as T
  }
  return Assets.load(path) as Promise<T>
}

export const useStarPixiAssets = (): StarPixiAssets => {
  const [assets, setAssets] = useState<StarPixiAssets>({
    textures: [],
    isLoaded: false,
  })

  useEffect(() => {
    let alive = true

    const loadAssets = async () => {
      try {
        const sheet1 = await loadAssetIfNotCached<Spritesheet>(assetPaths[0])
        await sheet1.parse()

        const sheet2 = await loadAssetIfNotCached<Spritesheet>(assetPaths[1])
        await sheet2.parse()

        const sheet3 = await loadAssetIfNotCached<Spritesheet>(assetPaths[2])
        await sheet3.parse()

        const sheet4 = await loadAssetIfNotCached<Spritesheet>(assetPaths[3])
        await sheet4.parse()

        const sheet5 = await loadAssetIfNotCached<Spritesheet>(assetPaths[4])
        await sheet5.parse()

        const sheet6 = await loadAssetIfNotCached<Spritesheet>(assetPaths[5])
        await sheet6.parse()

        const sheet7 = await loadAssetIfNotCached<Spritesheet>(assetPaths[6])
        await sheet7.parse()

        const sheets = [sheet1, sheet2, sheet3, sheet4, sheet5, sheet6, sheet7]

        const texturesMap = new Map<string, Texture>()
        for (const sheet of sheets) {
          for (const [key, texture] of Object.entries(sheet.textures)) {
            texturesMap.set(key, texture as Texture)
          }
        }

        const animationFrameNames =
          (sheets[0]?.data?.animations?.['star_01'] as string[]) ?? []

        const fallbackFrameNames = Array.from(texturesMap.keys()).sort()

        const frameNames =
          animationFrameNames.length > 0
            ? animationFrameNames
            : fallbackFrameNames

        const textures = frameNames
          .map((frameName) => texturesMap.get(frameName))
          .filter((texture): texture is Texture => texture !== undefined)

        if (!alive) return

        setAssets({
          textures,
          isLoaded: true,
        })
      } catch (error) {
        console.error('useStarPixiAssets: Failed to load star assets:', error)
        if (error instanceof Error) {
          console.error('useStarPixiAssets: Error message:', error.message)
          console.error('useStarPixiAssets: Error stack:', error.stack)
        }
      }
    }

    loadAssets()

    return () => {
      alive = false
    }
  }, [])

  return assets
}
